import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: false });

// Token fallback for browsers that block cross-site cookies — captured from /api/auth/session response
client.interceptors.request.use((config) => {
  const tok = typeof window !== "undefined" ? window.localStorage.getItem("garnacha_token") : null;
  if (tok) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

// Helper to trigger a browser download from an axios blob response (PDF, etc.)
async function downloadBlob(promise, fallbackName) {
  const r = await promise;
  const cd = r.headers?.["content-disposition"] || "";
  const m = /filename="?([^";]+)"?/i.exec(cd);
  const filename = m ? m[1] : fallbackName;
  const url = window.URL.createObjectURL(new Blob([r.data], { type: r.headers?.["content-type"] || "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// CSV export helper — triggers a browser download via cookie-authenticated GET
export const downloadExport = (module) => {
  const tok = typeof window !== "undefined" ? window.localStorage.getItem("garnacha_token") : null;
  const url = `${API}/export/${module}${tok ? `?_t=${tok}` : ""}`;
  // For downloads we still rely on the cookie — if blocked, ask user to use a different browser
  const a = document.createElement("a");
  a.href = `${API}/export/${module}`;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const downloadBackup = () => {
  const a = document.createElement("a");
  a.href = `${API}/export/backup/all`;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

// Bearer-authenticated invoices CSV export — reads the auth token from axios interceptor,
// downloads as a blob, then triggers a save dialog with the server-provided filename.
export const downloadInvoicesCsv = async (params = {}) => {
  const resp = await client.get("/invoices/export/csv", {
    params,
    responseType: "blob",
  });
  // Parse filename from Content-Disposition (falls back to a sane default)
  const cd = resp.headers?.["content-disposition"] || "";
  const match = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
  const fname = match ? match[1] : "invoices_export_all.csv";
  const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const api = {
  // Leads
  listLeads: () => client.get("/leads").then((r) => r.data),
  createLead: (data) => client.post("/leads", data).then((r) => r.data),
  updateLead: (id, data) => client.put(`/leads/${id}`, data).then((r) => r.data),
  deleteLead: (id) => client.delete(`/leads/${id}`).then((r) => r.data),
  convertLead: (id) => client.post(`/leads/${id}/convert`).then((r) => r.data),

  // Samples
  listSamples: () => client.get("/samples").then((r) => r.data),
  createSample: (data) => client.post("/samples", data).then((r) => r.data),
  updateSample: (id, data) => client.put(`/samples/${id}`, data).then((r) => r.data),
  deleteSample: (id) => client.delete(`/samples/${id}`).then((r) => r.data),

  // Customers
  listCustomers: () => client.get("/customers").then((r) => r.data),
  createCustomer: (data) => client.post("/customers", data).then((r) => r.data),
  updateCustomer: (id, data) => client.put(`/customers/${id}`, data).then((r) => r.data),
  deleteCustomer: (id) => client.delete(`/customers/${id}`).then((r) => r.data),

  // Tasks
  listTasks: () => client.get("/tasks").then((r) => r.data),
  createTask: (data) => client.post("/tasks", data).then((r) => r.data),
  updateTask: (id, data) => client.put(`/tasks/${id}`, data).then((r) => r.data),
  deleteTask: (id) => client.delete(`/tasks/${id}`).then((r) => r.data),

  // Notes
  listNotes: (params = {}) => client.get("/notes", { params }).then((r) => r.data),
  createNote: (data) => client.post("/notes", data).then((r) => r.data),
  deleteNote: (id) => client.delete(`/notes/${id}`).then((r) => r.data),

  // Products
  listProducts: (params = {}) => client.get("/products", { params }).then((r) => r.data),
  createProduct: (data) => client.post("/products", data).then((r) => r.data),
  updateProduct: (id, data) => client.put(`/products/${id}`, data).then((r) => r.data),
  deleteProduct: (id) => client.delete(`/products/${id}`).then((r) => r.data),
  seedDefaultProducts: () => client.post("/products/seed-defaults").then((r) => r.data),

  // Orders + Payments
  listOrders: (params = {}) => client.get("/orders", { params }).then((r) => r.data),
  createOrder: (data) => client.post("/orders", data).then((r) => r.data),
  availableBatchesForProduct: (pid) => client.get(`/products/${pid}/available-batches`).then((r) => r.data),
  updateOrder: (id, data) => client.put(`/orders/${id}`, data).then((r) => r.data),
  deleteOrder: (id) => client.delete(`/orders/${id}`).then((r) => r.data),
  registerPayment: (id, amount) => client.post(`/orders/${id}/payments`, { amount }).then((r) => r.data),
  markOrderPaid: (id) => client.post(`/orders/${id}/mark-paid`).then((r) => r.data),
  changeOrderStatus: (id, status) => client.patch(`/orders/${id}/status`, { status }).then((r) => r.data),
  customerFinance: (id) => client.get(`/customers/${id}/finance`).then((r) => r.data),
  customersFinanceSummary: () => client.get(`/customers-finance/summary`).then((r) => r.data),

  // Invoices
  listInvoices: () => client.get("/invoices").then((r) => r.data),
  invoicesForOrder: (orderId) => client.get(`/orders/${orderId}/invoices`).then((r) => r.data),
  invoicesForCustomer: (cid) => client.get(`/customers/${cid}/invoices`).then((r) => r.data),
  invoiceDraftFromOrder: (orderId) => client.get(`/invoices/draft/from-order/${orderId}`).then((r) => r.data),
  createInvoice: (data) => client.post("/invoices", data).then((r) => r.data),
  updateInvoice: (id, data) => client.put(`/invoices/${id}`, data).then((r) => r.data),
  deleteInvoice: (id) => client.delete(`/invoices/${id}`).then((r) => r.data),
  markInvoicePaid: (id) => client.post(`/invoices/${id}/mark-paid`).then((r) => r.data),
  changeInvoiceStatus: (id, status) => client.post(`/invoices/${id}/status`, { status }).then((r) => r.data),
  registerInvoicePayment: (id, amount) => client.post(`/invoices/${id}/payments`, { amount }).then((r) => r.data),
  sendInvoiceEmail: (id, override_email) => client.post(`/invoices/${id}/send-email`, null, { params: override_email ? { override_email } : {} }).then((r) => r.data),
  downloadInvoicePdf: (id, number) => downloadBlob(client.get(`/invoices/${id}/pdf`, { responseType: "blob" }), `${number || "invoice"}.pdf`),
  exportInvoicesCsv: (filters = {}) => downloadInvoicesCsv(filters),

  // Inventory
  listInventory: () => client.get("/inventory").then((r) => r.data),
  updateInventory: (id, data) => client.put(`/inventory/${id}`, data).then((r) => r.data),

  // Partner level
  recomputePartner: (customerId) => client.post(`/customers/${customerId}/recompute-partner-level`).then((r) => r.data),
  recomputeAllPartners: () => client.post(`/admin/recompute-all-partner-levels`).then((r) => r.data),

  // Production batches
  listProductionBatches: () => client.get("/production-batches").then((r) => r.data),
  createProductionBatch: (data) => client.post("/production-batches", data).then((r) => r.data),
  updateProductionBatch: (id, data) => client.put(`/production-batches/${id}`, data).then((r) => r.data),
  deleteProductionBatch: (id) => client.delete(`/production-batches/${id}`).then((r) => r.data),
  recallProductionBatch: (id, reason) => client.post(`/production-batches/${id}/recall`, { reason }).then((r) => r.data),

  // Omavalvonta (Self-monitoring)
  listHygieneChecks: () => client.get("/hygiene-checks").then((r) => r.data),
  createHygieneCheck: (data) => client.post("/hygiene-checks", data).then((r) => r.data),
  updateHygieneCheck: (id, data) => client.put(`/hygiene-checks/${id}`, data).then((r) => r.data),
  deleteHygieneCheck: (id) => client.delete(`/hygiene-checks/${id}`).then((r) => r.data),
  filterHygieneChecks: (params) => client.get("/hygiene-checks/filter", { params }).then((r) => r.data),
  downloadHygienePdf: (id) => downloadBlob(client.get(`/hygiene-checks/${id}/pdf`, { responseType: "blob" }), `hygiene-check-${id}.pdf`),
  downloadHygieneRangePdf: (params) => downloadBlob(client.get("/hygiene-checks-pdf/export", { params, responseType: "blob" }), `hygiene-checks-range.pdf`),

  listRawMaterialChecks: () => client.get("/raw-material-checks").then((r) => r.data),
  createRawMaterialCheck: (data) => client.post("/raw-material-checks", data).then((r) => r.data),
  updateRawMaterialCheck: (id, data) => client.put(`/raw-material-checks/${id}`, data).then((r) => r.data),
  deleteRawMaterialCheck: (id) => client.delete(`/raw-material-checks/${id}`).then((r) => r.data),
  filterRawMaterialChecks: (params) => client.get("/raw-material-checks/filter", { params }).then((r) => r.data),
  downloadRawMaterialPdf: (id) => downloadBlob(client.get(`/raw-material-checks/${id}/pdf`, { responseType: "blob" }), `raw-material-${id}.pdf`),
  downloadRawMaterialRangePdf: (params) => downloadBlob(client.get("/raw-material-checks-pdf/export", { params, responseType: "blob" }), `raw-material-range.pdf`),

  listIncidents: () => client.get("/incidents").then((r) => r.data),
  createIncident: (data) => client.post("/incidents", data).then((r) => r.data),
  updateIncident: (id, data) => client.put(`/incidents/${id}`, data).then((r) => r.data),
  deleteIncident: (id) => client.delete(`/incidents/${id}`).then((r) => r.data),
  filterIncidents: (params) => client.get("/incidents/filter", { params }).then((r) => r.data),
  downloadIncidentPdf: (id) => downloadBlob(client.get(`/incidents/${id}/pdf`, { responseType: "blob" }), `incident-${id}.pdf`),
  downloadIncidentRangePdf: (params) => downloadBlob(client.get("/incidents-pdf/export", { params, responseType: "blob" }), `incidents-range.pdf`),
  incidentAffectedCustomers: (id) => client.get(`/incidents/${id}/affected-customers`).then((r) => r.data),

  listLabTests: () => client.get("/lab-tests").then((r) => r.data),
  createLabTest: (data) => client.post("/lab-tests", data).then((r) => r.data),
  deleteLabTest: (id) => client.delete(`/lab-tests/${id}`).then((r) => r.data),

  // Traceability
  traceability: (params = {}) => client.get("/traceability", { params }).then((r) => r.data),

  // Follow ups
  listFollowUps: () => client.get("/followups").then((r) => r.data),

  // Dashboard + Meta
  getDashboard: () => client.get("/dashboard").then((r) => r.data),
  getMeta: () => client.get("/meta").then((r) => r.data),

  // Seed
  seed: (force = false) => client.post(`/seed${force ? "?force=true" : ""}`).then((r) => r.data),
};

export default api;
